"use client";

import { useRouter } from "next/navigation";

import type { SiteLocale } from "~/lib/graphql";

const languages = [
  { code: "en", label: "EN", fullName: "English" },
  { code: "de", label: "DE", fullName: "Deutsch" },
  { code: "it", label: "IT", fullName: "Italiano" },
];

interface LanguageSwitcherProps {
  currentLang: SiteLocale;
  availableLocales: SiteLocale[];
  currentSlug?: string;
  allSlugLocales?: Array<{
    locale: string;
    value: string;
  }>;
}

export function LanguageSwitcher({
  currentLang,
  availableLocales,
  currentSlug,
  allSlugLocales,
}: LanguageSwitcherProps) {
  const router = useRouter();

  const handleLanguageChange = (lang: SiteLocale) => {
    if (currentLang === lang) return; // Don't navigate if already on this language

    if (currentSlug && allSlugLocales) {
      // Find the slug for the target language
      const targetSlug =
        allSlugLocales.find((slugLocale) => slugLocale.locale === lang)?.value ||
        currentSlug; // Fallback to current slug if not found

      router.push(`/${lang}/page/${targetSlug}`);
    } else if (currentSlug) {
      // For subpages without allSlugLocales, use the current slug (old behavior)
      router.push(`/${lang}/page/${currentSlug}`);
    } else {
      // For the main page, just change the locale
      router.push(`/${lang}`);
    }
  };

  // Filter languages to only show available ones
  const availableLanguages = languages.filter((lang) =>
    availableLocales.includes(lang.code as SiteLocale),
  );

  // Don't show the switcher if there's only one language available
  if (availableLanguages.length <= 1) {
    return null;
  }

  return (
    <div className="ml-6 flex gap-2" role="group" aria-label="Language selection">
      {availableLanguages.map((lang) => {
        const isActive = currentLang === lang.code;
        return (
          <button
            key={lang.code}
            onClick={() => handleLanguageChange(lang.code as SiteLocale)}
            className={`h-8 w-8 rounded-full border-2 text-xs font-medium transition-all ${
              isActive
                ? "border-gray-600 bg-gray-600 text-white"
                : "cursor-pointer border-gray-300 bg-white text-gray-600 hover:border-gray-400"
            } `}
            aria-label={`Switch to ${lang.fullName}`}
            aria-current={isActive ? "page" : undefined}
            aria-pressed={isActive}
            title={`Switch to ${lang.fullName}`}
          >
            {lang.label}
          </button>
        );
      })}
    </div>
  );
}
